import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { auth as firebaseAuth } from '@/features/auth/config/firebase-config';
import { verifyPasswordResetCode, confirmPasswordReset, applyActionCode } from 'firebase/auth';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { logger } from '@/features/help/utils/logger';
import { Lock, AlertTriangle, CheckCircle, Eye, EyeOff, ArrowLeft } from 'lucide-react';

/**
 * AuthActionHandlerPage component handles Firebase email actions like password reset and email verification.
 */
export const AuthActionHandlerPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { clearAuthError } = useAuthStore();

  const [mode, setMode] = useState<string | null>(null);
  const [actionCode, setActionCode] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  // Ref to track if verifyPasswordResetCode was successful for the current actionCode in this component instance
  const successfullyVerifiedPasswordResetCodeRef = useRef<string | null>(null);

  useEffect(() => {
    const currentUrlMode = searchParams.get('mode');
    const currentUrlActionCode = searchParams.get('oobCode');

    logger.debug('AuthActionHandlerPage', 'useEffect triggered', {
      currentUrlMode,
      currentUrlActionCode,
      prevModeState: mode,
      prevActionCodeState: actionCode,
      refValue: successfullyVerifiedPasswordResetCodeRef.current,
    });

    clearAuthError();

    // Only reset message and related UI state if the mode or action code from URL truly changes,
    // or if we are not in a state where a password reset code was just verified.
    if (currentUrlMode !== mode || currentUrlActionCode !== actionCode) {
      logger.debug(
        'AuthActionHandlerPage',
        'Mode or ActionCode changed, resetting message & UI states.'
      );
      setMessage(null);
      setIsError(false);
      setVerificationSuccess(false);
      setShowPasswordForm(false);
      // If the mode is changing away from resetPassword or the code is different, clear the ref.
      if (
        currentUrlMode !== 'resetPassword' ||
        currentUrlActionCode !== successfullyVerifiedPasswordResetCodeRef.current
      ) {
        successfullyVerifiedPasswordResetCodeRef.current = null;
      }
    } else if (
      currentUrlMode === 'resetPassword' &&
      successfullyVerifiedPasswordResetCodeRef.current === currentUrlActionCode
    ) {
      logger.debug(
        'AuthActionHandlerPage',
        'Mode and ActionCode same, and password reset code was already verified. Preserving message.'
      );
      // Do not reset message if it's for an already verified password reset code waiting for input.
    } else {
      // Fallback to reset message if conditions above aren't met, to ensure clean state for other modes.
      logger.debug(
        'AuthActionHandlerPage',
        'Mode and ActionCode same, but not a previously verified password reset. Resetting message & form state.'
      );
      setMessage(null);
      setShowPasswordForm(false);
    }

    setIsPageLoading(true);

    if (!currentUrlMode || !currentUrlActionCode) {
      logger.warn('AuthActionHandlerPage', 'Missing mode or actionCode in URL parameters.');
      setMessage('Invalid action link. Mode or action code is missing.');
      setIsError(true);
      setIsPageLoading(false);
      setMode(currentUrlMode); // Still set for title consistency
      setActionCode(currentUrlActionCode);
      return;
    }

    // These state updates can trigger the useEffect to run again.
    // On the next run, the conditions at the top for resetting message should behave correctly.
    setMode(currentUrlMode);
    setActionCode(currentUrlActionCode);

    const handleAction = async () => {
      if (currentUrlMode === 'resetPassword') {
        if (successfullyVerifiedPasswordResetCodeRef.current === currentUrlActionCode) {
          logger.info(
            'AuthActionHandlerPage',
            '[Debug ResetPassword] Code already verified by this instance. Ensuring UI is ready for password input.',
            { actionCode: currentUrlActionCode }
          );
          setMessage('Please enter your new password.'); // Ensure the prompt is visible
          setShowPasswordForm(true); // Show the form
          setIsError(false); // Ensure no error state
          setIsPageLoading(false);
          return;
        }
        try {
          logger.info(
            'AuthActionHandlerPage',
            '[Debug ResetPassword] Verifying password reset code.',
            { actionCode: currentUrlActionCode }
          );
          await verifyPasswordResetCode(firebaseAuth, currentUrlActionCode);
          logger.info(
            'AuthActionHandlerPage',
            '[Debug ResetPassword] Password reset code verified successfully by Firebase.'
          );
          successfullyVerifiedPasswordResetCodeRef.current = currentUrlActionCode; // Mark as verified
          setMessage('Please enter your new password.');
          setShowPasswordForm(true); // Show the form
          setIsError(false);
        } catch (e: any) {
          logger.error(
            'AuthActionHandlerPage',
            '[Debug ResetPassword] Invalid or expired password reset code.',
            {
              errorCode: e.code,
              errorMessage: e.message,
              errorStack: e.stack,
              actionCode: currentUrlActionCode,
            }
          );
          successfullyVerifiedPasswordResetCodeRef.current = null; // Clear ref on error
          setShowPasswordForm(false); // Hide form on error
          setMessage(
            e.message ||
              'Invalid or expired password reset link. Please try resetting your password again.'
          );
          setIsError(true);
        }
      } else if (currentUrlMode === 'verifyEmail') {
        logger.info(
          'AuthActionHandlerPage',
          '[Debug VerifyEmail] Attempting to apply email verification code.',
          { actionCode: currentUrlActionCode, path: window.location.href }
        );
        try {
          await applyActionCode(firebaseAuth, currentUrlActionCode);
          logger.info(
            'AuthActionHandlerPage',
            '[Debug VerifyEmail] applyActionCode was truly successful AFTER await. Email should be verified. Navigating to /email-success.',
            { actionCode: currentUrlActionCode }
          );
          navigate('/email-success'); // Navigate immediately
        } catch (e: any) {
          if (e.code === 'auth/invalid-action-code') {
            logger.info(
              'AuthActionHandlerPage',
              "[Debug VerifyEmail] applyActionCode initially failed with auth/invalid-action-code. Will check user's verification status after reload.",
              { actionCode: currentUrlActionCode }
            );
            try {
              await firebaseAuth.currentUser?.reload(); // Force refresh user state
              if (firebaseAuth.currentUser?.emailVerified) {
                logger.info(
                  'AuthActionHandlerPage',
                  "[Debug VerifyEmail] User's email IS verified after reload. Firebase likely auto-applied the code. Proceeding to /email-success."
                );
                navigate('/email-success'); // Navigate immediately
              } else {
                logger.warn(
                  'AuthActionHandlerPage',
                  "[Debug VerifyEmail] User's email is NOT verified after reload, and applyActionCode failed with auth/invalid-action-code. Treating as a genuine invalid code.",
                  { actionCode: currentUrlActionCode }
                );
                setMessage(
                  'Failed to verify email. The link may be invalid, expired, or already used.'
                );
                setIsError(true);
                setVerificationSuccess(false);
              }
            } catch (reloadError: any) {
              logger.error(
                'AuthActionHandlerPage',
                '[Debug VerifyEmail] Error reloading user after initial auth/invalid-action-code from applyActionCode.',
                {
                  originalErrorCode: e.code,
                  reloadErrorCode: reloadError.code,
                  reloadErrorMessage: reloadError.message,
                  actionCode: currentUrlActionCode,
                }
              );
              setMessage(
                'An error occurred while checking email verification status. Please try logging in.'
              );
              setIsError(true);
              setVerificationSuccess(false);
            }
          } else {
            logger.error(
              'AuthActionHandlerPage',
              '[Debug VerifyEmail] Error during initial attempt of applyActionCode (not auth/invalid-action-code).',
              {
                errorCode: e.code,
                errorMessage: e.message,
                actionCode: currentUrlActionCode,
              }
            );
            setMessage(e.message || 'Failed to verify email. The link may be invalid or expired.');
            setIsError(true);
            setVerificationSuccess(false);
          }
        }
      } else {
        logger.warn('AuthActionHandlerPage', 'Unsupported action mode.', {
          mode: currentUrlMode,
        });
        setMessage(`Unsupported action: ${currentUrlMode}.`);
        setIsError(true);
      }
      setIsPageLoading(false); // End loading after action is handled
    };

    handleAction();
    // Added mode and actionCode (state) to dependencies. When setMode/setActionCode are called,
    // useEffect re-runs. The logic at the top of useEffect is designed to handle this gracefully,
    // especially for preserving the password input prompt.
  }, [searchParams, clearAuthError, navigate, mode, actionCode]);

  const handlePasswordResetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) {
      setMessage('Passwords do not match.');
      setIsError(true);
      setShowPasswordForm(true);
      return;
    }
    if (!actionCode) {
      setMessage('Action code is missing. Cannot reset password.');
      setIsError(true);
      setShowPasswordForm(false);
      return;
    }

    setIsPageLoading(true);
    clearAuthError();
    setMessage(null);
    setIsError(false);

    try {
      await confirmPasswordReset(firebaseAuth, actionCode, newPassword);
      logger.info('AuthActionHandlerPage', 'Password has been reset successfully.');
      setMessage(
        'Your password has been successfully updated! You can now log in with your new password.'
      );
      setVerificationSuccess(true);
      setShowPasswordForm(false);
      setIsError(false);
      setNewPassword('');
      setConfirmNewPassword('');
      setTimeout(() => navigate('/login'), 5000);
    } catch (err: any) {
      logger.error('AuthActionHandlerPage', 'Error resetting password.', {
        error: err,
      });
      let UImessage = 'Failed to reset password. Please try again.';
      if (err.code === 'auth/weak-password') {
        UImessage = 'The new password is too weak. Please choose a stronger password.';
      } else if (err.code === 'auth/invalid-action-code') {
        UImessage = 'The password reset link is invalid or has expired. Please request a new one.';
      }
      setMessage(UImessage);
      setIsError(true);
      setShowPasswordForm(true);
    } finally {
      setIsPageLoading(false);
    }
  };

  const renderContent = () => {
    if (isPageLoading && !message) {
      return (
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-lg text-gray-300">Processing your request...</p>
        </div>
      );
    }

    if (message) {
      return (
        <div
          className={`px-4 py-2 rounded-md mb-6 text-center ${isError ? 'bg-red-900 border border-red-700' : verificationSuccess ? 'bg-green-900 border border-green-700' : 'bg-gray-700 border border-gray-600'}`}
        >
          <div className="flex items-center justify-center mb-2">
            {isError ? (
              <AlertTriangle className="h-6 w-6 text-red-400 mr-2" />
            ) : verificationSuccess ? (
              <CheckCircle className="h-6 w-6 text-green-400 mr-2" />
            ) : null}
            <p
              className={`text-sm ${isError ? 'text-red-300' : verificationSuccess ? 'text-green-300' : 'text-gray-200'}`}
            >
              {message}
            </p>
          </div>
          {(isError || verificationSuccess) && mode !== 'resetPassword' && (
            <Link to="/login" className="font-medium text-blue-500 hover:text-blue-400">
              Go to Login
            </Link>
          )}
          {verificationSuccess && mode === 'resetPassword' && (
            <Link to="/login" className="font-medium text-blue-500 hover:text-blue-400">
              Proceed to Login
            </Link>
          )}
          {isError &&
            mode === 'resetPassword' &&
            !message?.includes('Enter a new password') &&
            !message?.includes('Passwords do not match.') && (
              <Link to="/login" className="font-medium text-blue-500 hover:text-blue-400">
                Back to Login to try again
              </Link>
            )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-gray-800 p-10 rounded-xl shadow-2xl relative">
        {mode === 'resetPassword' && (
          <button
            onClick={() => navigate('/login')}
            className="absolute top-4 left-4 text-gray-400 hover:text-white transition-colors z-10"
            aria-label="Go back to login"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
        )}
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
            {mode === 'resetPassword'
              ? 'Reset Your Password'
              : mode === 'verifyEmail'
                ? 'Verify Your Email'
                : 'Account Action'}
          </h2>
        </div>

        {renderContent()}

        {mode === 'resetPassword' && showPasswordForm && !verificationSuccess && (
          <form className="mt-8 space-y-6" onSubmit={handlePasswordResetSubmit}>
            <div className="rounded-md shadow-sm space-y-4">
              <div className="relative">
                <label htmlFor="new-password" className="sr-only">
                  New Password
                </label>
                <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  id="new-password"
                  name="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="appearance-none relative block w-full pl-10 pr-10 py-2 border border-gray-700 rounded-md bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="New Password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-200 focus:outline-none"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
                >
                  {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <div className="relative">
                <label htmlFor="confirm-new-password" className="sr-only">
                  Confirm New Password
                </label>
                <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  id="confirm-new-password"
                  name="confirmNewPassword"
                  type={showConfirmNewPassword ? 'text' : 'password'}
                  required
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  className="appearance-none relative block w-full pl-10 pr-10 py-2 border border-gray-700 rounded-md bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Confirm New Password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-200 focus:outline-none"
                  onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                  aria-label={
                    showConfirmNewPassword
                      ? 'Hide confirm new password'
                      : 'Show confirm new password'
                  }
                >
                  {showConfirmNewPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
            <div>
              <button
                type="submit"
                disabled={isPageLoading}
                className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white 
                ${isPageLoading ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'} 
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
              >
                {isPageLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Resetting Password...
                  </div>
                ) : (
                  'Set New Password'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
